import React, { useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, UserCheck, UserX, Clock } from 'lucide-react';
import { useAdminUsers, AdminUser } from '@/hooks/admin/useAdminUsers'; // Importer le hook et le type
import { useTranslation } from 'react-i18next'; // <-- Importer
import { format } from 'date-fns';

const UserManagementTab: React.FC = () => {
  const { users, isLoading, error, updateUserStatus } = useAdminUsers();
  const { t } = useTranslation(); // <-- Utiliser
  const [isUpdating, setIsUpdating] = useState<string | null>(null); // Pour l'état de chargement par ligne

  const handleStatusUpdate = async (userId: string, status: 'active' | 'suspended' | 'pending') => {
    setIsUpdating(userId);
    await updateUserStatus(userId, status);
    setIsUpdating(null);
  };

  const getStatusBadgeVariant = (status: 'active' | 'suspended' | 'pending'): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'active':
        return 'default'; // Ou une couleur verte custom
      case 'suspended':
        return 'destructive';
      case 'pending':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  if (isLoading) {
    return <div>{t('admin.loading_users') || 'Loading users...'}</div>;
  }

  if (error) {
    return <div className="text-destructive">{t('admin.error_loading_users') || 'Error loading users:'} {error}</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">{t('admin.user_management') || 'User Management'}</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('admin.users.display_name') || 'Display Name'}</TableHead>
            <TableHead>{t('admin.users.email') || 'Email'}</TableHead>
            <TableHead>{t('admin.users.role') || 'Role'}</TableHead>
            <TableHead>{t('admin.users.subscription') || 'Subscription'}</TableHead>
            <TableHead>{t('admin.users.verified') || 'Verified'}</TableHead>
            <TableHead>{t('admin.users.created_at') || 'Created At'}</TableHead>
            <TableHead>{t('admin.users.status') || 'Status'}</TableHead>
            <TableHead><span className="sr-only">{t('admin.actions') || 'Actions'}</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.displayName}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell><Badge variant="outline">{user.role}</Badge></TableCell>
              <TableCell><Badge variant="secondary">{user.subscriptionTier}</Badge></TableCell>
              <TableCell>{user.isVerified ? <UserCheck className="h-5 w-5 text-green-500" /> : <UserX className="h-5 w-5 text-muted-foreground" />}</TableCell>
              <TableCell>{format(user.createdAt, 'PPP')}</TableCell>
              <TableCell>
                <Badge variant={getStatusBadgeVariant(user.status)}>{user.status}</Badge>
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0" disabled={isUpdating === user.id}>
                      <span className="sr-only">{t('admin.open_menu') || 'Open menu'}</span>
                      {isUpdating === user.id ? <Clock className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {user.status !== 'active' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(user.id, 'active')}>
                        {t('admin.actions.activate') || 'Activate'}
                      </DropdownMenuItem>
                    )}
                    {user.status !== 'suspended' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(user.id, 'suspended')}>
                        {t('admin.actions.suspend') || 'Suspend'}
                      </DropdownMenuItem>
                    )}
                     {user.status !== 'pending' && (
                      <DropdownMenuItem onClick={() => handleStatusUpdate(user.id, 'pending')}>
                        {t('admin.actions.set_pending') || 'Set Pending'}
                      </DropdownMenuItem>
                    )}
                    {/* Ajouter d'autres actions si nécessaire (ex: Voir profil, Supprimer) */}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserManagementTab; 